import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { LiveKitRoom } from "@livekit/components-react";
import "@livekit/components-styles";
import {
    Highlighter,
    Loader2,
    MessageSquare,
    PhoneOff,
    Puzzle,
    Sparkles,
    Circle,
    AlertTriangle,
    Captions,
    CaptionsOff,
    Check,
    Link2,
    X,
} from "lucide-react";

import { sessionService } from "../../application/SessionApplicationService";
import { saveSessionWhiteboard } from "../../application/useSessionWhiteboards";
import { Session } from "../../domain/Session";
import type { MeetingAccess } from "../../domain/meetingAccess";
import {
    endSessionMeeting,
    MEETING_ROOM_OPTIONS,
    requestSessionMeetingToken,
    setSessionTranscription,
    startSessionMeeting,
} from "../../application/meetings";
import { useAiInsightsGate } from "../../../ai_insights";
import RecommendationsPanel from "../components/RecommendationsPanel";
import SessionAIChatPanel from "../components/AddForm/SessionAIChatPanel";
import SessionToolsPanel from "../components/AddForm/SessionToolsPanel";
import MeetingStage from "../components/MeetingStage";
import MeetingProcessingNotice from "../components/MeetingProcessingNotice";
import {
    MeetingBadge,
    MeetingError,
    MeetingHeader,
    MeetingRoom,
} from "../components/MeetingChrome";
import { MeetingElapsed } from "../components/MeetingTimer";
import { MIN_SESSION_AUDIO_MINUTES } from "../../domain/audioMinimum";
import PatientHighlightsTab from "../components/PatientHighlightsTab";
import ConfirmDialog from "../../../../shared/ui/modal/ConfirmDialog";
import { subjectSessionsPath } from "../../domain/sessionsPath";
import ShareLinkDialog from "../../../../shared/ui/share/ShareLinkDialog";
import { LAYOUT } from "../../../../shared/layout";
import MeetingTrackAnnouncer from "../components/MeetingTrackAnnouncer";
import { useCompactRoom } from "../../application/useCompactRoom";
import { useEmdrSets } from "../../../emdr";
import type { TherapistToolBoard } from "../../domain/meetingToolBoard";

type Panel = "recommendations" | "ai-chat" | "highlights" | "tools";

/**
 * A session held in AlchelyMeeting, on its own page.
 *
 * Opened in its own tab rather than inside the wizard: a call is not a step of
 * a form, and the therapist should be able to leave the rest of Alchely alone
 * — or use it in the other tab — while the session runs.
 *
 * The chrome is deliberately narrow. Everything a therapist reaches for mid
 * session is in the dock; everything else would only compete with the person
 * on screen.
 *
 * Every root is `translate="no"`. A browser translating the page replaces the
 * text nodes React is holding, and the next render inserts a sibling before a
 * node the DOM no longer owns — which crashes the page. Losing a session to
 * that is not a trade worth making for copy that is already in the
 * therapist's language.
 */
export default function MeetingSessionPage() {
    const { t } = useTranslation();
    const { sessionId } = useParams<{ sessionId: string }>();
    const navigate = useNavigate();
    const { adopted: memoryAdopted } = useAiInsightsGate();

    const [access, setAccess] = useState<MeetingAccess | null>(null);
    const [session, setSession] = useState<Session | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [activePanel, setActivePanel] = useState<Panel | null>(null);
    /*
     * Held here rather than in the stage: the tools panel lists the meditation
     * library beside the other adopted tools, and it sits outside the LiveKit
     * context the stage lives in.
     */
    const [meditationOpen, setMeditationOpen] = useState(false);
    /* The same, for EMDR's bilateral stimulation panel. */
    const [bilateralOpen, setBilateralOpen] = useState(false);
    /* The same, for a tool's board on the stage. */
    const [toolBoard, setToolBoard] = useState<TherapistToolBoard | null>(null);
    const compactRoom = useCompactRoom();
    const [finishing, setFinishing] = useState(false);
    /**
     * Set when the call is over: `true` when a recording is on its way to the
     * pipeline, `false` when the session simply ended and nothing is coming.
     */
    const [finished, setFinished] = useState<boolean | null>(null);
    const [shareOpen, setShareOpen] = useState(false);
    /**
     * Whether the session is being recorded — the answer once the room exists,
     * as opposed to the one the token gave, which could only promise it. Null
     * until the room is up.
     */
    const [recording, setRecording] = useState<boolean | null>(null);
    /**
     * The answer the switch gave, once it has been pressed in this call. Null
     * until then, so the session's own value is what shows — the same shape as
     * `recording` above, and for the same reason: a local copy seeded from the
     * session would have two sources of truth and no way to tell which is the
     * newer one.
     */
    const [transcription, setTranscription] = useState<boolean | null>(null);
    /** Open while the therapist is being asked to confirm turning it off. */
    const [confirmingOff, setConfirmingOff] = useState(false);
    const [switching, setSwitching] = useState(false);

    /**
     * Where the patient joins: a signed link that needs no account, minted by
     * the API rather than built here, so only one place decides what a join
     * link looks like.
     */
    const patientLink = access?.share_url ?? "";

    useEffect(() => {
        if (!sessionId) return;
        let cancelled = false;

        requestSessionMeetingToken(sessionId)
            .then((meetingAccess) => {
                if (!cancelled) setAccess(meetingAccess);
            })
            .catch((err: unknown) => {
                if (cancelled) return;
                const status = (err as { response?: { status?: number } })
                    ?.response?.status;
                setError(
                    status === 503
                        ? t("session.alchelyMeeting.notConfigured")
                        : status === 403
                          ? t("session.alchelyMeeting.notAdopted")
                          : t("scheduler.meeting.unavailable")
                );
            });

        // The panels are all about the patient, so they need the session's
        // subject before any of them can render.
        sessionService.getSessionById(sessionId)
            .then((sessionData) => {
                if (!cancelled) setSession(sessionData);
            })
            .catch(() => {
                // The call still works without the side panels.
            });

        return () => {
            cancelled = true;
        };
    }, [sessionId, t]);

    /**
     * The room is up. Nothing could be recorded before this moment — LiveKit
     * creates a room when the first participant joins — so this is where a
     * recording actually starts, and where the badge stops guessing.
     */
    const connected = () => {
        if (!sessionId) return;
        startSessionMeeting(sessionId)
            .then((started) => setRecording(started.recording))
            .catch(() => setRecording(false));
    };

    /**
     * Turn this session's transcription off, or back on.
     *
     * The server is what decides, and the answer it gives is what the room
     * shows: turning it back on is permission, not a promise — consent, the
     * tool and a room that exists still decide whether anything records — so
     * the badge is set from the response rather than from the request.
     */
    const switchTranscription = async (enabled: boolean) => {
        if (!sessionId || switching) return;
        setSwitching(true);
        try {
            const answer = await setSessionTranscription(sessionId, enabled);
            setTranscription(answer.transcription_enabled);
            setRecording(answer.recording);
        } catch {
            // Nothing changed on the server, so nothing changes here: showing
            // "not being transcribed" over a recording that is still running
            // is the one wrong answer this screen can give.
        } finally {
            setSwitching(false);
            setConfirmingOff(false);
        }
    };

    /**
     * Put the board on the session record.
     *
     * Only reached from the therapist's own save button — a board is never
     * written on its own, so what is not saved leaves no trace when the room
     * closes.
     */
    const saveBoard = async (imageBase64: string) => {
        if (!sessionId) throw new Error("No session to save the board to.");
        await saveSessionWhiteboard(sessionId, imageBase64);
    };

    /**
     * Where a finished session goes next.
     *
     * The server settles what state it is left in, because only it knows
     * whether a recording is actually on its way: a recorded session stays
     * `processing` until the worker has transcribed it, and one that was never
     * recorded is closed, since nothing would ever pick it up. Deciding that
     * here would be deciding it from a promise made before anyone joined.
     */
    const finish = async () => {
        if (!sessionId || finishing) return;
        setFinishing(true);
        // EMDR ratings chosen and not saved yet go into the session first.
        await emdrSets.flushPending?.().catch(() => undefined);
        try {
            const ended = await endSessionMeeting(sessionId);
            // The server is the one that knows whether notes are coming, and
            // the screen says only what is true: the pipeline's promise when
            // something was recorded, and plainly that the session is over
            // when it was not.
            setFinished(Boolean(ended.recording));
        } catch {
            // A room that would not close still empties on its own once
            // everyone leaves; it is not worth trapping the therapist in a
            // call that is over. Nothing can be promised about a request that
            // failed, so nothing is.
            setFinished(false);
        }
    };

    // Whoever the session is with — one patient, a couple or a group. Every
    // panel works for all three: memory reads the group's own sessions the
    // way it reads a couple's.
    /**
     * Whether this session is being transcribed at all: what the switch last
     * answered, or — until it has been pressed here — what the session itself
     * carries, so reloading the call cannot quietly re-arm a recording the
     * therapist turned off.
     */
    const transcriptionEnabled =
        transcription ?? session?.transcription_enabled !== false;

    const groupId = session?.group_patient_id ?? null;
    // From the record the session points at, not the `type` label beside it.
    const isCouple = !groupId && Boolean(session?.couple_patient_id);
    const subjectKind: "patient" | "couple" | "group" = groupId ? "group" : isCouple ? "couple" : "patient";
    const subjectId = groupId
        ? groupId
        : isCouple
          ? (session?.couple_patient_id ?? null)
          : (session?.patient_id ?? null);
    const toolSubject = subjectId ? { kind: subjectKind, id: subjectId } : null;

    /*
     * EMDR ratings are kept on one patient's session only — a couple's or a
     * group's is not erased when one of its people asks to be — so the panel
     * is only given somewhere to save them on an individual session.
     */
    const individualSession = subjectKind === "patient" && Boolean(session?.patient_id);
    const emdrSets = useEmdrSets(individualSession ? (sessionId ?? null) : null);

    // Recommendations and the chat are Memoria aumentada's, so they follow its
    // adoption — the same rule the wizard's dock uses. Highlights and the tools
    // catalogue stand on their own.
    const panels: { key: Panel; label: string; icon: React.ReactNode }[] = [
        ...(memoryAdopted && subjectId
            ? [
                  {
                      key: "recommendations" as const,
                      label: t("recommendations.title"),
                      icon: <Sparkles className="w-5 h-5" />,
                  },
                  {
                      key: "ai-chat" as const,
                      label: t("patient.tabs.aiChat"),
                      icon: <MessageSquare className="w-5 h-5" />,
                  },
              ]
            : []),
        ...(subjectId
            ? [
                  {
                      key: "highlights" as const,
                      label: t("session.highlights"),
                      icon: <Highlighter className="w-5 h-5" />,
                  },
              ]
            : []),
        {
            key: "tools",
            label: t("session.tools"),
            icon: <Puzzle className="w-5 h-5" />,
        },
    ];

    if (error) {
        return (
            <MeetingRoom>
                <MeetingHeader title={t("session.alchelyMeeting.title")} />
                <MeetingError
                    message={error}
                    action={{ label: t("common.close"), onClick: () => window.close() }}
                />
            </MeetingRoom>
        );
    }

    // The call is over. Everything that belonged to it — the room, the dock,
    // the share link — goes with it.
    if (finished !== null && sessionId) {
        return (
            <MeetingRoom>
                <MeetingHeader
                    title={session?.title ?? t("session.alchelyMeeting.title")}
                    subtitle={t("session.alchelyMeeting.finishedSubtitle")}
                />
                <MeetingProcessingNotice
                    sessionId={sessionId}
                    recording={finished}
                    onOpenSession={() => navigate(`/session/${sessionId}`)}
                    onGoToSessions={() => navigate(subjectSessionsPath(session ?? {}))}
                />
            </MeetingRoom>
        );
    }

    /**
     * Whether a dock panel is actually on screen.
     *
     * Derived once rather than asked twice: the panel needs somebody the
     * session is with, so a panel can be chosen and still not open, and a
     * narrow screen that hid the call for a panel that never appeared would
     * be a therapist looking at an empty room.
     */
    const panelOpen = activePanel !== null && (activePanel === "tools" || !!subjectId);

    /**
     * Open the meditation chooser from the tools panel.
     *
     * On a phone the panel takes the whole screen and the call behind it is
     * hidden, and the chooser lives on the call's stage — so leaving the panel
     * up opened the library where nobody could see it, and the therapist was
     * left tapping a tool that seemed to do nothing. There the panel is put
     * away so the chooser is the next thing on screen. On a wider screen the
     * panel sits beside the call and can stay.
     */
    const openMeditation = () => {
        setMeditationOpen(true);
        setBilateralOpen(false);
        if (compactRoom) setActivePanel(null);
    };

    /**
     * Open the bilateral stimulation panel, for the same reasons. The two
     * panels share the right of the stage, so opening one puts the other away.
     */
    const openBilateral = () => {
        setBilateralOpen(true);
        setMeditationOpen(false);
        if (compactRoom) setActivePanel(null);
    };

    /**
     * Put a tool's board on the stage, for both people.
     *
     * The panel goes away on every screen, not only a phone's: the board
     * shares the stage with a column of faces, and a 380px panel beside both
     * leaves the board too narrow to arrange anything on. The meditation and
     * bilateral panels go too, as they do for each other — they would sit
     * over the board.
     */
    const closeToolBoard = () => setToolBoard(null);

    const openBoard = (board: TherapistToolBoard) => {
        setToolBoard(board);
        setMeditationOpen(false);
        setBilateralOpen(false);
        setActivePanel(null);
    };

    return (
        <MeetingRoom className="flex-row">
            {/*
              * The panel and the call share the width on a laptop. On a phone
              * there is no width to share — a 380px panel beside a 56px dock
              * leaves the call about ten pixels — so there the panel takes
              * the screen and the call is one press away again, the same
              * bargain the chat already makes with the video.
              */}
            <main
                className={`min-w-0 flex-1 flex-col ${
                    panelOpen ? "hidden sm:flex" : "flex"
                }`}
            >
                <MeetingHeader
                    title={session?.title ?? t("session.alchelyMeeting.title")}
                    subtitle={
                        /* Only alongside a live recording: telling a therapist
                           who is not being recorded how long a recording has to
                           run would answer a question they do not have. */
                        access && (recording ?? access.recording)
                            ? t("session.minAudio.notice", {
                                  minutes: MIN_SESSION_AUDIO_MINUTES,
                              })
                            : undefined
                    }
                >
                    {/* The clock belongs here, as context. It used to sit in
                        the middle of the control bar between the microphone
                        and the whiteboard, which made the one thing on screen
                        you cannot press look like the things you can. */}
                    <MeetingElapsed
                        room={sessionId ? `sess-${sessionId}` : null}
                        running={recording !== null}
                    />

                    {/* Said in the room rather than left to be discovered
                        afterwards: it decides whether an analysis follows the
                        session at all. */}
                    <MeetingBadge
                        tone={memoryAdopted ? "ok" : "live"}
                        icon={
                            memoryAdopted ? (
                                <Check size={12} />
                            ) : (
                                <AlertTriangle size={12} />
                            )
                        }
                        title={
                            memoryAdopted
                                ? t("session.alchelyMeeting.memoryEnabledHint")
                                : t("session.alchelyMeeting.memoryDisabledHint")
                        }
                    >
                        {t("session.alchelyMeeting.memoryStatus")}:{" "}
                        {memoryAdopted
                            ? t("session.alchelyMeeting.memoryEnabled")
                            : t("session.alchelyMeeting.memoryDisabled")}
                    </MeetingBadge>

                    {access &&
                        (!transcriptionEnabled ? (
                            /* A different answer from "no consent on file",
                               and the badge says which: this one is the
                               therapist's own, and they can undo it here. */
                            <MeetingBadge
                                tone="warn"
                                icon={<CaptionsOff size={12} />}
                                title={t(
                                    "session.alchelyMeeting.transcriptionOffHint"
                                )}
                            >
                                {t("session.alchelyMeeting.notTranscribing")}
                            </MeetingBadge>
                        ) : (recording ?? access.recording) ? (
                            <MeetingBadge
                                tone="live"
                                icon={<Circle size={8} className="fill-current" />}
                            >
                                {t("session.alchelyMeeting.recording")}
                            </MeetingBadge>
                        ) : (
                            /* Said out loud, because the whole point of the
                               session is the notes it produces. */
                            <MeetingBadge
                                tone="warn"
                                icon={<AlertTriangle size={12} />}
                                title={t("session.alchelyMeeting.notRecordingHint")}
                            >
                                {t("session.alchelyMeeting.notRecording")}
                            </MeetingBadge>
                        ))}

                    {/* The switch itself, beside the badge that reports it.
                        Turning it off is asked about first — the recording
                        stops and its audio is discarded, and neither comes
                        back — while turning it back on is not, since nothing
                        is lost by it. */}
                    <button
                        type="button"
                        onClick={() =>
                            transcriptionEnabled
                                ? setConfirmingOff(true)
                                : switchTranscription(true)
                        }
                        disabled={switching}
                        aria-pressed={transcriptionEnabled}
                        title={t(
                            transcriptionEnabled
                                ? "session.alchelyMeeting.transcriptionTurnOff"
                                : "session.alchelyMeeting.transcriptionTurnOn"
                        )}
                        className="room-control min-h-9 text-xs disabled:opacity-50"
                    >
                        {transcriptionEnabled ? (
                            <Captions size={14} aria-hidden="true" />
                        ) : (
                            <CaptionsOff size={14} aria-hidden="true" />
                        )}
                        <span>
                            {t(
                                transcriptionEnabled
                                    ? "session.alchelyMeeting.transcriptionOn"
                                    : "session.alchelyMeeting.transcriptionOff"
                            )}
                        </span>
                    </button>

                    <button
                        type="button"
                        onClick={() => setShareOpen(true)}
                        disabled={!patientLink}
                        title={patientLink}
                        className="room-control min-h-9 text-xs disabled:opacity-50"
                    >
                        <Link2 size={14} aria-hidden="true" />
                        <span>{t("session.alchelyMeeting.shareLink")}</span>
                    </button>
                </MeetingHeader>

                <ShareLinkDialog
                    isOpen={shareOpen}
                    onClose={() => setShareOpen(false)}
                    title={t("session.alchelyMeeting.share.title")}
                    subtitle={t("session.alchelyMeeting.share.subtitle")}
                    status={patientLink ? "ready" : "loading"}
                    url={patientLink || null}
                    message={t("session.alchelyMeeting.share.message", { url: patientLink })}
                />

                {access ? (
                    // Centred and width-capped: a two-person call stretched
                    // across an ultrawide monitor is mostly empty desk. A
                    // tool's board is the exception — it wants every pixel.
                    <div
                        className="mx-auto flex min-h-0 w-full max-w-5xl flex-1 flex-col p-2 sm:p-4 has-[[data-tool-board]]:max-w-none"
                        data-testid="meeting-room"
                    >
                        <LiveKitRoom
                            data-lk-theme="default"
                            options={MEETING_ROOM_OPTIONS}
                            serverUrl={access.url}
                            token={access.token}
                            connect={true}
                            video={true}
                            audio={true}
                            onConnected={connected}
                            className="h-full"
                        >
                            {/* A voice that joins later is a track the first
                                announcement never saw. */}
                            <MeetingTrackAnnouncer onAudioPublished={connected} />
                            {/* Finishing is the page's job here, and it does
                                more than disconnect. */}
                            <MeetingStage
                                canLeave={false}
                                canManageBoard
                                onSaveBoard={saveBoard}
                                meditationOpen={meditationOpen}
                                onMeditationOpenChange={setMeditationOpen}
                                bilateralOpen={bilateralOpen}
                                onBilateralOpenChange={setBilateralOpen}
                                emdrRecorder={individualSession ? emdrSets : undefined}
                                toolBoard={toolBoard}
                                onToolBoardClose={closeToolBoard}
                            />
                        </LiveKitRoom>
                    </div>
                ) : (
                    <div className="flex flex-1 items-center justify-center">
                        <Loader2 className="h-6 w-6 animate-spin text-room-ink-muted" />
                        <span className="sr-only">{t("common.loading")}</span>
                    </div>
                )}

                {/* Ending the session is not an operating control and never
                    sits beside one: mute and the board are pressed dozens of
                    times a session, this once, and a misfire costs the call. */}
                <div className="flex shrink-0 justify-end border-t border-room-line-soft px-4 py-2.5">
                    <button
                        type="button"
                        onClick={finish}
                        disabled={finishing}
                        className="room-control room-control-end disabled:opacity-50"
                    >
                        <PhoneOff size={16} aria-hidden="true" />
                        <span>{t("session.alchelyMeeting.finish")}</span>
                    </button>
                </div>
            </main>

            {/* A room surface, with no `dark:` variant. This was
                `bg-white dark:bg-gray-900` inside a room that is dark in both
                themes, so a therapist in light mode — the default — got a
                380px sheet of white beside their patient's face.

                `room-panel` re-points the semantic tokens for everything
                inside, so the app components the dock opens theme themselves
                for the room without knowing they are in one. */}
            {/* The tools stand on their own; every other panel needs somebody
                the session is with. Gating the tools too once left a group
                call with a button that opened nothing. */}
            {panelOpen && (
                <aside className="room-panel relative flex w-full shrink-0 flex-col overflow-hidden border-l border-room-line-soft p-4 sm:w-[380px]">
                    <button
                        type="button"
                        onClick={() => setActivePanel(null)}
                        aria-label={t("common.close")}
                        className="absolute right-3 top-3 z-30 flex size-9 items-center justify-center rounded-lg border border-room-line bg-room-base text-room-ink-muted transition-colors duration-[--duration-fast] hover:bg-room-hover hover:text-room-ink"
                    >
                        <X className="h-4 w-4" aria-hidden="true" />
                    </button>

                    <div className="min-h-0 flex-1 overflow-y-auto pt-10">
                        {activePanel === "recommendations" && subjectId && (
                            <RecommendationsPanel
                                patientId={subjectId}
                                subjectKind={subjectKind}
                                className="!w-full !h-full !sticky-0"
                            />
                        )}
                        {/* Kept mounted: the chat holds a conversation, and
                            remounting it mid session would discard it. */}
                        {subjectId && (
                            <div className={activePanel === "ai-chat" ? undefined : "hidden"}>
                                {subjectKind === "group" ? (
                                    <SessionAIChatPanel groupPatientId={subjectId} />
                                ) : isCouple ? (
                                    <SessionAIChatPanel couplePatientId={subjectId} />
                                ) : (
                                    <SessionAIChatPanel patientId={subjectId} />
                                )}
                            </div>
                        )}
                        {activePanel === "highlights" && subjectId && (
                            <PatientHighlightsTab
                                patientId={subjectId}
                                isCouple={isCouple}
                                isGroup={subjectKind === "group"}
                            />
                        )}
                        {activePanel === "tools" && (
                            <SessionToolsPanel
                                sessionId={sessionId ?? null}
                                onOpenMeditation={openMeditation}
                                onOpenBilateral={openBilateral}
                                onOpenBoard={openBoard}
                                session={{
                                    type: session?.type,
                                    couple_patient_id: session?.couple_patient_id,
                                }}
                                subject={
                                    toolSubject
                                        ? { ...toolSubject, label: session?.title ?? undefined }
                                        : null
                                }
                            />
                        )}
                    </div>
                </aside>
            )}

            <nav
                aria-label={t("session.alchelyMeeting.dock")}
                className="flex w-14 shrink-0 flex-col items-center gap-2 border-l border-room-line-soft py-3"
            >
                {panels.map((panel) => (
                    <button
                        key={panel.key}
                        type="button"
                        onClick={() =>
                            setActivePanel((current) =>
                                current === panel.key ? null : panel.key
                            )
                        }
                        title={panel.label}
                        aria-label={panel.label}
                        aria-pressed={activePanel === panel.key}
                        style={{
                            minHeight: LAYOUT.TOUCH_TARGET_SIZE,
                            minWidth: LAYOUT.TOUCH_TARGET_SIZE,
                        }}
                        className={`flex items-center justify-center rounded-xl border border-transparent transition-colors duration-[--duration-fast] ${
                            activePanel === panel.key
                                ? "bg-room-accent text-white"
                                : "text-room-ink-muted hover:bg-room-hover hover:text-room-ink"
                        }`}
                    >
                        {panel.icon}
                    </button>
                ))}
            </nav>

            {/* Said plainly, because none of it can be undone afterwards: the
                recording stops, what it captured is thrown away, and this
                session gets no automatic notes. */}
            <ConfirmDialog
                isOpen={confirmingOff}
                destructive
                isBusy={switching}
                title={t("session.alchelyMeeting.transcriptionOffTitle")}
                message={t("session.alchelyMeeting.transcriptionOffMessage")}
                confirmLabel={t("session.alchelyMeeting.transcriptionOffConfirm")}
                onConfirm={() => switchTranscription(false)}
                onCancel={() => setConfirmingOff(false)}
            />
        </MeetingRoom>
    );
}
